import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { useUpgradePrompt } from '../../../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../../config/music24PlanCapabilities';
import { useOpenSongPage } from '../../../../../smartLinks/catalog/song/page/useOpenSongPage';
import { downloadMediaUrl } from '../../../inlineMediaUrl';
import { openPublishDialog } from '../../../publish/publishDialogStore';
import { useMakeKitAssets } from '../../assets/useMakeKitAssets';
import { planDayDate } from '../rolloutDayCopy';
import { ChannelMark } from './ChannelMark';
import { MilestoneRow } from './MilestoneRow';
import { useRollout } from './RolloutContext';
import { rolloutMessages } from './rolloutMessages';
import { hourInZone } from './rolloutTime';
import type { MilestoneFacts } from './useMilestoneFacts';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The video credits of a lyric video (a full-song video, `VIDEO_CREDIT_COST`). */
const LYRIC_VIDEO_CREDITS = 3;

/**
 * The lyric video milestone (design rollout-plan a2, owner decision 10): it always shows. Locked
 * below Pro, "Make the lyric video" when the kit has none, "Making · N%", "Publish on release day"
 * when it is made, the set record ("Private now · public at 00:00", "Change time"), or live.
 */
const LyricVideoMilestoneComponent: React.FC<{ facts: MilestoneFacts; isLast: boolean }> = ({ facts, isLast }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openUpgrade = useUpgradePrompt();
  const openSongPage = useOpenSongPage();
  const { accountId, item, plan, timeZone, canAct } = useRollout();
  const { make, isMaking } = useMakeKitAssets({ accountId, catalogItemUuid: item.uuid });
  const { lyricVideo, lyricPublish } = facts;
  const releaseDay = plan.releaseDate === null ? '' : planDayDate(intl, plan.releaseDate);

  const openPublish = useCallback(() => {
    if (lyricVideo === null) {
      return;
    }
    openPublishDialog({
      accountId,
      kitAssetUuid: lyricPublish?.kitAssetUuid ?? lyricVideo.uuid,
      catalogItemUuid: item.uuid,
      channel: 'youtube',
      assetName: intl.formatMessage(rolloutMessages.lyricTitle),
      downloadUrl: lyricVideo.fileUrl === null ? null : downloadMediaUrl(lyricVideo.fileUrl),
    });
  }, [accountId, intl, item.uuid, lyricPublish?.kitAssetUuid, lyricVideo]);

  let kind: 'youtube' | 'locked' | 'done' = 'youtube';
  let content: React.ReactNode;
  const mark = <ChannelMark platform="youtube" small />;
  const fromKit = <span className={styles.msrKind}>{intl.formatMessage(rolloutMessages.fromKit)}</span>;

  if (!facts.canMakeFullVideos && lyricVideo === null) {
    kind = 'locked';
    content = (
      <>
        <FontAwesomeIcon icon={faLock} className={styles.msrLocked} />
        <b className={styles.msrLocked}>{intl.formatMessage(rolloutMessages.lyricTitle)}</b>
        <span className={styles.msrSub}>{intl.formatMessage(rolloutMessages.lyricLocked)}</span>
        <span className={styles.grow} />
        <button
          type="button"
          className={styles.link}
          onClick={() => openUpgrade(SMLUpgradeFeature.fullVideo, item.title)}
        >
          {intl.formatMessage(rolloutMessages.seePro)}
        </button>
      </>
    );
  } else if (lyricVideo === null) {
    const needsMaster = item.song.masterStatus !== 'ready';
    const needsLyrics = !item.song.hasLyrics;
    let action: React.ReactNode;
    if (needsMaster || needsLyrics) {
      action = (
        <SMLButton onClick={() => openSongPage(item.uuid, needsMaster ? 'files' : 'lyrics')}>
          {intl.formatMessage(needsMaster ? rolloutMessages.lyricNeedsMaster : rolloutMessages.lyricNeedsLyrics)}
        </SMLButton>
      );
    } else {
      action = (
        <>
          <SMLButton
            icon={<FontAwesomeIcon icon={faSparkles} />}
            disabled={!canAct || isMaking}
            onClick={() => {
              make([{ themeId: 'lyricVideo', params: {} }], false).catch(console.error);
            }}
          >
            {intl.formatMessage(rolloutMessages.lyricMake)}
          </SMLButton>
          <span className={styles.cost}>
            {intl.formatMessage(rolloutMessages.lyricCost, { credits: LYRIC_VIDEO_CREDITS })}
          </span>
        </>
      );
    }
    content = (
      <>
        {fromKit}
        {mark}
        <b>{intl.formatMessage(rolloutMessages.lyricTitle)}</b>
        <span className={styles.msrSub}>{intl.formatMessage(rolloutMessages.lyricNone)}</span>
        <span className={styles.grow} />
        {action}
      </>
    );
  } else if (lyricVideo.status !== 'ready') {
    content = (
      <>
        {fromKit}
        {mark}
        <b>{intl.formatMessage(rolloutMessages.lyricTitle)}</b>
        <span className={clsx(styles.st, styles.stWk)}>
          {intl.formatMessage(rolloutMessages.making, { percent: lyricVideo.progress })}
        </span>
        <span className={styles.msrSub}>{intl.formatMessage(rolloutMessages.lyricMaking)}</span>
      </>
    );
  } else {
    const live = lyricPublish?.status === 'published';
    if (live) {
      kind = 'done';
    }
    let state: React.ReactNode;
    if (live) {
      state = <span className={clsx(styles.pub, styles.pubDone)}>{intl.formatMessage(rolloutMessages.lyricLive)}</span>;
    } else if (lyricPublish !== null && lyricPublish.publishAt !== null) {
      state = (
        <span className={styles.pub}>
          {intl.formatMessage(rolloutMessages.lyricSet, { time: hourInZone(intl, lyricPublish.publishAt, timeZone) })}
        </span>
      );
    } else {
      state = <span className={styles.msrSub}>{intl.formatMessage(rolloutMessages.lyricNotSet)}</span>;
    }
    let action: React.ReactNode = null;
    if (!live && !facts.youtubeConnected) {
      action = (
        <button type="button" className={styles.link} onClick={() => router.push(SMLRoutes.promoteChannels)}>
          {intl.formatMessage(rolloutMessages.connectYouTube)}
        </button>
      );
    } else if (!live) {
      action = (
        <button type="button" className={styles.link} disabled={!canAct} onClick={openPublish}>
          {intl.formatMessage(
            lyricPublish === null ? rolloutMessages.lyricPublishOnDay : rolloutMessages.lyricChangeTime
          )}
        </button>
      );
    }
    content = (
      <>
        {fromKit}
        {mark}
        <b>{intl.formatMessage(rolloutMessages.lyricPublic)}</b>
        {state}
        <span className={styles.grow} />
        {action}
      </>
    );
  }

  return (
    <MilestoneRow kind={kind} when="00:00" day={releaseDay} isLast={isLast}>
      {content}
    </MilestoneRow>
  );
};

export const LyricVideoMilestone = TypedMemo(LyricVideoMilestoneComponent);
